import React from "react";
import parse, {
  domToReact,
  HTMLReactParserOptions,
  Element,
} from "html-react-parser";

// Define the Tailwind CSS classes you want to apply for each original class
const tailwindClasses: Record<string, string> = {
  "be-ix-link-block": "mx-auto",
  "be-related-link-container": "flex flex-col items-center gap-5",
  "be-label": "font-heading text-[32px] tracking-[1.92px] text-olive",
  "be-list": "grid grid-cols-1 gap-5 lg:grid-cols-3",
  "be-list-item":
    "hover:bg-burntOrange group flex flex-col items-stretch p-5 rounded-xl bg-white shadow-default transition max-w-[450px]",
  "be-related-link":
    "font-body text-lg font-bold tracking-[0.2px] text-olive group-hover:text-white",
  "be-related-meta-desc":
    "font-body tracking-[0.16px] text-midnight group-hover:text-white line-clamp-2",
};

interface ReactElementProps extends React.HTMLAttributes<HTMLElement> {
  className: string;
  class?: string;
  href?: string;
}

const BrightEdgeContent = ({ htmlContent }: { htmlContent: string }) => {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element && domNode.attribs) {
        const originalClassNames = domNode.attribs.class?.split(" ") || [];
        const tailwindClassNames = originalClassNames
          .map((className) => tailwindClasses[className] || className)
          .join(" ");

        // Prepare the props for the React element
        const props: ReactElementProps = {
          ...domNode.attribs,
          className: tailwindClassNames,
        };
        delete props.class; // Remove the 'class' attribute, use 'className' instead

        // Special handling for 'be-label': transform it into an h2 tag
        if (originalClassNames.includes("be-label")) {
          domNode.tagName = "h2"; // Change the tag name to 'h2'
        }

        // Special handling for 'be-list': transform it into a div tag
        if (originalClassNames.includes("be-list")) {
          domNode.tagName = "div"; // Change the tag name to 'div'
        }

        // Special handling for 'be-related-link': transform it into an h3 tag
        if (originalClassNames.includes("be-related-link")) {
          domNode.tagName = "h3"; // Change the tag name to 'h3'
          // Additionally, remove the href attribute from props if it exists
          delete props.href;
        }

        // Special handling for 'be-list-item': transform it into a link if it contains a 'be-related-link'
        if (originalClassNames.includes("be-list-item")) {
          const relatedLink = domNode.children.find(
            (child) =>
              child instanceof Element &&
              child.attribs.class?.includes("be-related-link")
          ) as Element | undefined;

          if (relatedLink) {
            // Extract the href from the related link
            const href = relatedLink.attribs.href;

            // Replace the tag name with 'a' and add the href attribute
            domNode.tagName = "a";
            props.href = href;
          }
        }

        return React.createElement(
          domNode.tagName,
          props,
          domToReact(domNode.children as Element[], options)
        );
      }
    },
  };

  return <>{parse(htmlContent, options)}</>;
};

export default BrightEdgeContent;
